import type { IUserDataFormatSettings } from "@framework/types";

import type { IExternalTooltipContent } from "../../../../../../../components/common/chart-plugins";
import { formatMeasurementNumber } from "../../../utils/measurement-format";
import type { IChartData } from "../interfaces";

const PHASE_UNIT_CODE = 133;
const RPM_UNIT_CODE = 140;
const DEGREE_SYMBOL = "\u00B0";
const ZERO_FRACTION_REGEX = /[.,]0+$/u;
const HIDE_EXTERNAL_TOOLTIP_MARKER = null;

export interface IAmplitudePhaseTooltipPair {
  title: string;
  amplitude?: number;
  amplitudeUnitCode?: number | string | null;
  amplitudeUnits?: string;
  phase?: number;
  rotationFrequencyRpm?: number | null;
}

interface IFormatAmplitudePhaseTooltipContentOptions {
  dataFormat: IUserDataFormatSettings | null | undefined;
  rpmUnitLabel: string;
}

const isPhaseSeries = (series: IChartData): boolean => String(series.unitCode) === String(PHASE_UNIT_CODE);

const getAmplitudePhaseTooltipKey = (series: IChartData, date: string): string => {
  return [series.parameterName ?? "", series.idPoint ?? series.name, date].join("|");
};

export const buildAmplitudePhaseTooltipPairs = (seriesList: IChartData[]): Map<string, IAmplitudePhaseTooltipPair> => {
  const pairs = new Map<string, IAmplitudePhaseTooltipPair>();

  seriesList.forEach(series => {
    if (series.isAmplitudePhase !== true) {
      return;
    }

    const seriesIsPhase = isPhaseSeries(series);

    series.items.forEach(item => {
      const key = getAmplitudePhaseTooltipKey(series, item.date);
      const pair = pairs.get(key) ?? { title: series.name };

      pair.title = series.name;

      if (seriesIsPhase) {
        pair.phase = item.value;
      } else {
        pair.amplitude = item.value;
        pair.amplitudeUnitCode = series.unitCode ?? null;
        pair.amplitudeUnits = series.units;
      }

      if (pair.rotationFrequencyRpm == null && item.rotationFrequencyRpm != null) {
        pair.rotationFrequencyRpm = item.rotationFrequencyRpm;
      }

      pairs.set(key, pair);
    });
  });

  return pairs;
};

export const findAmplitudePhaseTooltipPair = (
  pairs: Map<string, IAmplitudePhaseTooltipPair>,
  series: IChartData | undefined,
  date: string | undefined,
): IAmplitudePhaseTooltipPair | null => {
  if (!series || series.isAmplitudePhase !== true || !date) {
    return null;
  }

  return pairs.get(getAmplitudePhaseTooltipKey(series, date)) ?? null;
};

export const formatAmplitudePhaseTooltipContent = (
  pair: IAmplitudePhaseTooltipPair,
  { dataFormat, rpmUnitLabel }: IFormatAmplitudePhaseTooltipContentOptions,
): IExternalTooltipContent | null => {
  if (pair.amplitude == null || pair.phase == null) {
    return null;
  }

  const formattedAmplitude = formatMeasurementNumber(pair.amplitude, pair.amplitudeUnitCode ?? null, dataFormat, {
    fallbackDigits: 2,
  });
  const formattedPhase = formatMeasurementNumber(pair.phase, PHASE_UNIT_CODE, dataFormat, { fallbackDigits: 0 });

  if (!formattedAmplitude || !formattedPhase) {
    return null;
  }

  const lines = [`${formattedAmplitude}${pair.amplitudeUnits ?? ""} / ${formattedPhase}${DEGREE_SYMBOL}`];
  const rotationFrequencyRpm = pair.rotationFrequencyRpm;

  if (rotationFrequencyRpm != null && Number.isFinite(rotationFrequencyRpm) && rotationFrequencyRpm > 0) {
    const formattedRpm = formatMeasurementNumber(rotationFrequencyRpm, RPM_UNIT_CODE, dataFormat, {
      fallbackDigits: 0,
    }).replace(ZERO_FRACTION_REGEX, "");

    if (formattedRpm) {
      lines.push(`${formattedRpm} ${rpmUnitLabel}`);
    }
  }

  return {
    titleLines: [pair.title],
    bodyLines: lines.map(text => ({ text, color: HIDE_EXTERNAL_TOOLTIP_MARKER })),
  };
};
